import { produce } from 'immer';
import _ from 'lodash';

import { RESET_STATE } from 'store/global/actions';
import { CHECKLIST_FULL_ENTITY_ACTION_TYPES, CHECKLISTS_ACTION_TYPE } from 'store/system/actions';

const initialState = {};

// A detail read is only as deep as the selected node needs, so a shallower payload carries
// `categories: null` (and a category-depth one carries `items: null`). Those nulls mean "not
// fetched", never "empty" — overwriting the stored branches with them would empty the tree.
const mergeChecklistCategories = (previous, next) =>
  next.map(category => {
    const previousCategory = _.find(previous, { id: category.id });

    return category.items ? category : { ...category, items: previousCategory?.items ?? null };
  });

const mergeChecklistRead = (previous, next) => {
  if (!previous) {
    return next;
  }

  const categories = next.categories
    ? mergeChecklistCategories(previous.categories, next.categories)
    : previous.categories;

  return { ...previous, ...next, categories };
};

const checklistsReducer = (state = initialState, { type, payload, meta }) =>
  produce(state, draft => {
    switch (type) {
      case CHECKLISTS_ACTION_TYPE.GET_CHECKLISTS:
        return payload || initialState;

      case CHECKLISTS_ACTION_TYPE.DELETE_CHECKLIST:
        delete draft[meta.checklistId];
        break;

      case CHECKLISTS_ACTION_TYPE.DELETE_CHECKLIST_CATEGORY: {
        const { checklistId, categoryId } = meta;

        const categoryIndex = _.findIndex(draft[checklistId].categories, { categoryId });

        if (categoryIndex !== -1) {
          draft[checklistId].categories.splice(categoryIndex, 1);
        }

        break;
      }

      case CHECKLISTS_ACTION_TYPE.DELETE_CHECKLIST_ITEM: {
        const { checklistId, categoryId, itemId: id } = meta;

        const categoryIndex = _.findIndex(draft[checklistId].categories, { categoryId });
        const index = _.findIndex(draft[checklistId].categories[categoryIndex]?.items, { id });

        if (index !== -1) {
          draft[checklistId].categories[categoryIndex].items.splice(index, 1);
        }

        break;
      }

      // A node read answers with the node alone, so it is merged in place. The tree is already loaded
      // (the list read carries the shape, just no expression bodies), so the node's own checklistId /
      // categoryId are enough to find its seat.
      case CHECKLISTS_ACTION_TYPE.GET_CHECKLIST_NODE: {
        const { checklistId, itemId } = meta;
        const categories = draft[checklistId]?.categories;

        if (!categories) {
          break;
        }

        const categoryIndex = _.findIndex(categories, {
          id: itemId ? payload.categoryId : payload.id,
        });

        if (categoryIndex === -1) {
          break;
        }

        if (!itemId) {
          // Its items are a different read's answer, and this one did not ask for them.
          categories[categoryIndex] = { ...payload, items: categories[categoryIndex].items };
          break;
        }

        const itemIndex = _.findIndex(categories[categoryIndex].items, { id: payload.id });

        if (itemIndex !== -1) {
          categories[categoryIndex].items[itemIndex] = payload;
        }

        break;
      }

      case RESET_STATE:
        return initialState;

      default: {
        if (CHECKLIST_FULL_ENTITY_ACTION_TYPES.has(type)) {
          draft[payload.id] = mergeChecklistRead(draft[payload.id], payload);
          break;
        }

        return state;
      }
    }
  });

export default checklistsReducer;
